/**
 * OsPinAdmin — OS-INV-14
 *
 * Sub-component rendered inside /os/inventory (staff-authenticated).
 * Collapsible "Device PINs" panel. Wraps the admin PIN API (Firebase staff auth
 * via authFetch — same pattern as the rest of Inventory.tsx).
 *
 * API contracts (OS-INV-09 — locked):
 *   POST  /api/os/admin/pins          body { name? }  → 201 { id, name, pin, expiresAt }
 *   GET   /api/os/admin/pins          → [{ id, name, expiresAt, createdAt, revokedAt, active }]
 *   PATCH /api/os/admin/pins/:id      body { name }   → { id, name, ... }
 *   POST  /api/os/admin/pins/:id/revoke               → { id, revokedAt, ... }
 *
 * Security: the raw PIN is shown ONCE on creation, then gone. The "write it
 * down" warning is prominent — large monospace display, explicit warning text,
 * copy-to-clipboard button.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';

// ── Types ──────────────────────────────────────────────────────────────────

interface PinRecord {
  id: string;
  name: string;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  active: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export const OsPinAdmin: React.FC = () => {
  const { apiBase } = useTenant();
  const [open, setOpen] = useState(false);
  const [pins, setPins] = useState<PinRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Generate dialog
  const [genOpen, setGenOpen] = useState(false);
  const [genName, setGenName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [newPin, setNewPin] = useState<{ pin: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rename state (inline per row)
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // Revoke confirm dialog
  const [revokePin, setRevokePin] = useState<PinRecord | null>(null);
  const [revoking, setRevoking] = useState(false);

  // ── Load pins ──────────────────────────────────────────────────────────────
  const loadPins = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await authFetch(`${apiBase}/admin/pins`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: PinRecord[] = await res.json();
      setPins(data);
    } catch (e: unknown) {
      setListError((e as Error).message ?? 'Failed to load PINs');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (open && pins.length === 0) loadPins();
  }, [open]);  

  // ── Generate PIN ──────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${apiBase}/admin/pins`, {
        method: 'POST',
        body: JSON.stringify(genName.trim() ? { name: genName.trim() } : {}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: { id: string; name: string; pin: string; expiresAt: string } = await res.json();
      setNewPin({ pin: data.pin, name: data.name });
      setGenName('');
      // Re-fetch the full list so the new row has complete, correct fields
      // (createdAt/active/revokedAt). The POST response only returns
      // {id,name,pin,expiresAt}; optimistically inserting that partial object
      // caused "Invalid Date" + a false "Expired" chip and hid the revoke button.
      await loadPins();
    } catch (e: unknown) {
      setListError((e as Error).message ?? 'Failed to generate PIN');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!newPin) return;
    navigator.clipboard.writeText(newPin.pin).then(() => {
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleGenClose = () => {
    setGenOpen(false);
    setNewPin(null);
    setCopied(false);
  };

  // ── Rename ────────────────────────────────────────────────────────────────
  const startRename = (pin: PinRecord) => {
    setRenamingId(pin.id);
    setRenameValue(pin.name);
    setRenameError(null);
  };

  const submitRename = async () => {
    if (!renameValue.trim()) { setRenameError('Name cannot be empty.'); return; }
    setRenameLoading(true);
    setRenameError(null);
    try {
      const res = await authFetch(`${apiBase}/admin/pins/${renamingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: PinRecord = await res.json();
      setPins(prev => prev.map(p => p.id === updated.id ? updated : p));
      setRenamingId(null);
    } catch (e: unknown) {
      setRenameError((e as Error).message ?? 'Failed to rename');
    } finally {
      setRenameLoading(false);
    }
  };

  // ── Remove (soft-delete) ────────────────────────────────────────────────────
  // Revokes the PIN (if active) AND removes it from the list. The row + its
  // audit-trail references are preserved server-side (DELETE sets hidden_at).
  const handleRemove = async () => {
    if (!revokePin) return;
    setRevoking(true);
    try {
      const res = await authFetch(`${apiBase}/admin/pins/${revokePin.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Drop it from the visible list (it's hidden server-side now).
      setPins(prev => prev.filter(p => p.id !== revokePin.id));
      setRevokePin(null);
    } catch (e: unknown) {
      setListError((e as Error).message ?? 'Failed to remove PIN');
    } finally {
      setRevoking(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
      {/* Panel header / toggle */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: open ? 'grey.100' : 'transparent',
          '&:hover': { bgcolor: 'grey.50' },
        }}
        onClick={() => setOpen(o => !o)}
        role="button"
        aria-expanded={open}
        aria-controls="pin-admin-panel"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        <Typography fontWeight={700} variant="subtitle1">
          Device PINs
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {pins.filter(p => p.active).length > 0 && (
            <Chip
              label={`${pins.filter(p => p.active).length} active`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      <Collapse in={open} id="pin-admin-panel">
        <Box sx={{ p: 2 }}>
          {/* Generate button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => { setGenOpen(true); setNewPin(null); }}
            sx={{ mb: 2, bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
          >
            Generate New PIN
          </Button>

          {loading && <CircularProgress size={24} sx={{ display: 'block', mb: 2 }} />}
          {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

          {/* PIN list */}
          <Stack spacing={1}>
            {pins.map(pin => (
              <PinRow
                key={pin.id}
                pin={pin}
                renamingId={renamingId}
                renameValue={renameValue}
                renameLoading={renameLoading}
                renameError={renameError}
                onStartRename={() => startRename(pin)}
                onRenameChange={setRenameValue}
                onRenameSubmit={submitRename}
                onRenameCancel={() => setRenamingId(null)}
                onRevoke={() => setRevokePin(pin)}
              />
            ))}
            {!loading && pins.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No PINs generated yet.
              </Typography>
            )}
          </Stack>
        </Box>
      </Collapse>

      {/* Generate PIN dialog */}
      <Dialog
        open={genOpen}
        onClose={!generating && !newPin ? handleGenClose : undefined}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>
          {newPin ? 'PIN Generated' : 'Generate New PIN'}
        </DialogTitle>
        <DialogContent>
          {!newPin ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optional: give this PIN a name (e.g. "Warehouse #2", "Keisha").
                If left blank, the server assigns the next Device number.
              </Typography>
              <TextField
                label="Device name (optional)"
                placeholder="Device N"
                value={genName}
                onChange={e => setGenName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !generating && handleGenerate()}
                fullWidth
                autoFocus
              />
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Write this PIN down — it will NOT be shown again.</strong>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                PIN for <strong>{newPin.name}</strong>:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="h4"
                  fontFamily="monospace"
                  fontWeight={700}
                  letterSpacing="0.25em"
                  sx={{ flex: 1, textAlign: 'center', userSelect: 'all' }}
                  role="status"
                  aria-label={`Generated PIN: ${newPin.pin}`}
                >
                  {newPin.pin}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton onClick={handleCopy} size="small">
                    {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!newPin ? (
            <>
              <Button onClick={handleGenClose} color="inherit" disabled={generating}>Cancel</Button>
              <Button
                onClick={handleGenerate}
                variant="contained"
                disabled={generating}
                startIcon={generating ? <CircularProgress size={16} /> : undefined}
                sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
              >
                Generate
              </Button>
            </>
          ) : (
            <Button
              onClick={handleGenClose}
              variant="contained"
              sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Remove confirm dialog */}
      <Dialog
        open={Boolean(revokePin)}
        onClose={() => !revoking && setRevokePin(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Remove PIN?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove PIN <strong>{revokePin?.name}</strong> from the list? Any active
            device sessions using this PIN are invalidated immediately. The PIN is
            kept in audit history but no longer appears here. If a device needs
            access again, generate a new PIN.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokePin(null)} color="inherit" disabled={revoking}>Cancel</Button>
          <Button
            onClick={handleRemove}
            variant="contained"
            color="error"
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ── PinRow ────────────────────────────────────────────────────────────────────

interface PinRowProps {
  pin: PinRecord;
  renamingId: string | null;
  renameValue: string;
  renameLoading: boolean;
  renameError: string | null;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onRenameSubmit: () => void;
  onRenameCancel: () => void;
  onRevoke: () => void;
}

const PinRow: React.FC<PinRowProps> = ({
  pin,
  renamingId,
  renameValue,
  renameLoading,
  renameError,
  onStartRename,
  onRenameChange,
  onRenameSubmit,
  onRenameCancel,
  onRevoke,
}) => {
  const isRenaming = renamingId === pin.id;
  const expired = !pin.active;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        opacity: expired ? 0.6 : 1,
        borderRadius: 2,
        bgcolor: expired ? 'grey.50' : 'white',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        {/* Main content */}
        <Box sx={{ flex: 1 }}>
          {isRenaming ? (
            <TextField
              value={renameValue}
              onChange={e => onRenameChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') onRenameSubmit();
                if (e.key === 'Escape') onRenameCancel();
              }}
              size="small"
              error={Boolean(renameError)}
              helperText={renameError}
              autoFocus
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={onRenameSubmit}
                      disabled={renameLoading || !renameValue.trim()}
                    >
                      {renameLoading ? <CircularProgress size={14} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={onRenameCancel} color="inherit">×</Button>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Typography fontWeight={600} variant="body2" sx={{ textDecoration: expired ? 'line-through' : 'none' }}>
              {pin.name}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              Created {new Date(pin.createdAt).toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">·</Typography>
            <Typography variant="caption" color={expired ? 'error.main' : 'text.secondary'}>
              {pin.revokedAt
                ? `Revoked ${new Date(pin.revokedAt).toLocaleDateString()}`
                : `Expires ${new Date(pin.expiresAt).toLocaleDateString()}`}
            </Typography>
          </Stack>
        </Box>

        {/* Status chip */}
        <Chip
          label={pin.active ? 'Active' : (pin.revokedAt ? 'Revoked' : 'Expired')}
          size="small"
          color={pin.active ? 'success' : 'default'}
          variant={pin.active ? 'filled' : 'outlined'}
        />

        {/* Actions */}
        {!isRenaming && (
          <Stack direction="row" spacing={0.5}>
            {pin.active && (
              <Tooltip title="Rename">
                <IconButton size="small" onClick={onStartRename} aria-label={`Rename ${pin.name}`}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Remove from list (revokes + hides; preserved in audit history). Always available. */}
            <Tooltip title="Remove from list">
              <IconButton size="small" onClick={onRevoke} color="error" aria-label={`Remove ${pin.name}`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
